(() => {
  const initialize = (section) => {
    if (section.dataset.enhanced === 'true') return;
    const track = section.querySelector('[data-gallery-track]');
    const slides = Array.from(section.querySelectorAll('[data-media-id]'));
    const thumbnails = Array.from(section.querySelectorAll('[data-media-target]'));
    let activeIndex = 0;
    const setActiveMedia = (index, scroll = true) => {
      if (!track || !slides.length) return;
      activeIndex = (index + slides.length) % slides.length;
      thumbnails.forEach((thumbnail, position) => {
        thumbnail.classList.toggle('is-active', position === activeIndex);
        if (position === activeIndex) thumbnail.setAttribute('aria-current', 'true');
        else thumbnail.removeAttribute('aria-current');
      });
      if (scroll) track.scrollTo({ left: activeIndex * track.clientWidth, behavior: 'instant' });
      slides.forEach((slide, position) => {
        if (position !== activeIndex) slide.querySelectorAll('video').forEach((video) => video.pause());
      });
    };
    thumbnails.forEach((thumbnail, index) => thumbnail.addEventListener('click', (event) => {
      event.preventDefault();
      setActiveMedia(index);
    }));
    section.querySelectorAll('[data-gallery-direction]').forEach((button) => button.addEventListener('click', () => setActiveMedia(activeIndex + Number(button.dataset.galleryDirection))));
    if (track) {
      let scrollFrame;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(() => setActiveMedia(Math.round(track.scrollLeft / track.clientWidth), false));
      }, { passive: true });
      track.addEventListener('keydown', (event) => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        setActiveMedia(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
      });
    }
    const dialog = section.querySelector('.size-guide');
    const opener = section.querySelector('[data-size-guide-open]');
    opener?.addEventListener('click', () => { dialog?.showModal(); dialog?.querySelector('[data-size-guide-close]')?.focus(); });
    dialog?.addEventListener('close', () => opener?.focus());
    dialog?.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      const items = Array.from(dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]')).filter(item => item.getClientRects().length);
      const first = items[0], last = items[items.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    section.querySelector('[data-size-guide-close]')?.addEventListener('click', () => dialog?.close());
    dialog?.addEventListener('click', (event) => {
      const bounds = dialog.getBoundingClientRect();
      if (event.target === dialog && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) dialog.close();
    });
    const dataElement = section.querySelector('[data-product-data]');
    if (!dataElement) {
      section.dataset.enhanced = 'true';
      return;
    }
    let data;
    try { data = JSON.parse(dataElement.textContent); } catch { return; }
    if (!Array.isArray(data.variants) || !data.strings) return;
    section.dataset.enhanced = 'true';
    const form = section.querySelector('[data-product-form]');
    const select = section.querySelector('[data-variant-select]');
    const options = Array.from(section.querySelectorAll('[data-option-index]'));
    const price = section.querySelector('[data-product-price]');
    const compare = section.querySelector('[data-product-compare]');
    const stickyPrice = section.querySelector('[data-sticky-price]');
    const submit = section.querySelector('[data-add-button]');
    const submitText = section.querySelector('[data-add-text]');
    const quantity = section.querySelector('[data-quantity]');
    const status = section.querySelector('[data-product-status]');
    const error = section.querySelector('[data-product-error]');
    const addPrice = section.querySelector('[data-add-price]');
    const tierCards = Array.from(section.querySelectorAll('[data-bundle-card]'));
    const tiers = Array.isArray(data.tiers) ? data.tiers : [];
    const hasBundles = tiers.length === 3 && tiers.every((tier) => tier.variants.length);
    const initialVariant = data.variants.find((variant) => String(variant.id) === String(select.value));
    let selectedVariant = initialVariant;
    let selectedTier = hasBundles ? tiers[0] : null;
    let busy = false;
    const rememberedSizes = Array(3).fill(initialVariant?.options[0]);
    const selectedOptions = () => options.map((option) => option.querySelector('input:checked')?.value);
    const activeCard = () => tierCards.find((card) => Number(card.dataset.bundleCard) === selectedTier?.count);
    const updateVariant = (variant, updateUrl = true) => {
      selectedVariant = variant;
      if (variant && !Array.from(select.options).some((option) => option.value === String(variant.id))) {
        select.querySelector('[data-bundle-variant]')?.remove();
        const bundleOption = new Option(`${selectedTier.name} — ${variant.options.join(' / ')}`, String(variant.id));
        bundleOption.dataset.bundleVariant = 'true';
        select.add(bundleOption);
      }
      select.value = variant ? String(variant.id) : '';
      submit.disabled = busy || !variant?.available;
      submitText.textContent = busy ? data.strings.adding : variant ? (variant.available ? data.strings.add : data.strings.soldOut) : data.strings.unavailable;
      status.textContent = variant ? (variant.available ? '' : selectedTier?.count > 1 ? data.strings.bundleSoldOut : data.strings.soldOutDetail) : data.strings.unavailableDetail;
      error.hidden = true;
      if (variant) {
        price.innerHTML = variant.price;
        stickyPrice.innerHTML = variant.price;
        const comparison = hasBundles && selectedTier.count > 1 ? activeCard()?.querySelector('[data-bundle-compare]')?.innerHTML : variant.compare;
        compare.innerHTML = comparison || '';
        compare.hidden = !comparison;
        if (addPrice) {
          addPrice.hidden = !hasBundles;
          addPrice.innerHTML = variant.price;
        }
        quantity.min = hasBundles ? 1 : variant.quantityMin || 1;
        quantity.step = hasBundles ? 1 : variant.quantityStep || 1;
        if (hasBundles) {
          quantity.max = 1;
          quantity.value = 1;
        } else {
          if (variant.quantityMax != null) quantity.max = variant.quantityMax;
          else quantity.removeAttribute('max');
          const minimum = Number(quantity.min);
          const step = Number(quantity.step);
          let value = Math.max(minimum, Number(quantity.value) || minimum);
          value = minimum + Math.ceil((value - minimum) / step) * step;
          if (quantity.max) value = Math.min(Number(quantity.max), value);
          quantity.value = value;
        }
        if (!hasBundles || selectedTier.count === 1) {
          options.forEach((option, index) => {
            option.querySelectorAll('input').forEach((input) => { input.checked = input.value === variant.options[index]; });
          });
        }
        if (hasBundles) {
          activeCard()?.querySelectorAll('[data-bundle-option]').forEach((input, index) => {
            input.value = variant.options[index];
            rememberedSizes[index] = variant.options[index];
          });
          const cardPrice = activeCard()?.querySelector('[data-bundle-price]');
          if (cardPrice) cardPrice.innerHTML = variant.price;
        }
        if (variant.mediaId) {
          const index = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(variant.mediaId));
          if (index >= 0) setActiveMedia(index);
        }
      } else {
        price.textContent = data.strings.unavailable;
        stickyPrice.textContent = data.strings.unavailable;
        compare.hidden = true;
        if (addPrice) addPrice.hidden = true;
      }
      options.forEach((option, index) => {
        const selected = selectedOptions();
        option.querySelector('[data-selected-option]').textContent = selected[index] || '';
        option.querySelectorAll('input').forEach((input) => {
          const available = data.variants.some((candidate) => candidate.available && candidate.options.every((value, candidateIndex) => value === (candidateIndex === index ? input.value : selected[candidateIndex])));
          input.closest('label').dataset.unavailable = String(!available);
        });
      });
      if (hasBundles) {
        const card = activeCard();
        const selected = Array.from(card.querySelectorAll('[data-bundle-option]')).map((input) => input.value);
        card.querySelectorAll('[data-bundle-option]').forEach((input, index) => {
          Array.from(input.options).forEach((option) => {
            const available = selectedTier.variants.some((candidate) => candidate.available && candidate.options.every((value, candidateIndex) => value === (candidateIndex === index ? option.value : selected[candidateIndex])));
            option.textContent = available ? option.value : `${option.value} — ${data.strings.soldOut}`;
          });
        });
      }
      if (updateUrl && section.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        if (hasBundles && selectedTier.count > 1) {
          url.searchParams.set('bundle', selectedTier.count);
          if (variant) url.searchParams.set('bundle_variant', variant.id);
          else url.searchParams.delete('bundle_variant');
          const single = data.variants.find((candidate) => candidate.options[0] === rememberedSizes[0]);
          if (single) url.searchParams.set('variant', single.id);
        } else {
          if (variant) url.searchParams.set('variant', variant.id);
          else url.searchParams.delete('variant');
          if (hasBundles) url.searchParams.set('bundle', '1');
          url.searchParams.delete('bundle_variant');
        }
        window.history.replaceState({}, '', url);
      }
    };
    const chooseTier = (count, variantId, updateUrl = true) => {
      selectedTier = tiers.find((tier) => tier.count === count) || tiers[0];
      tierCards.forEach((card) => {
        const selected = Number(card.dataset.bundleCard) === selectedTier.count;
        card.classList.toggle('is-selected', selected);
        card.querySelector('[data-bundle-choice]').checked = selected;
        card.querySelector('[data-bundle-details]').hidden = !selected;
        card.querySelectorAll('[data-bundle-option]').forEach((input, index) => {
          input.disabled = !selected;
          if (selected && rememberedSizes[index] && Array.from(input.options).some((option) => option.value === rememberedSizes[index])) input.value = rememberedSizes[index];
        });
      });
      const values = Array.from(activeCard().querySelectorAll('[data-bundle-option]')).map((input) => input.value);
      const variant = selectedTier.variants.find((candidate) => String(candidate.id) === String(variantId)) || selectedTier.variants.find((candidate) => candidate.options.every((value, index) => value === values[index]));
      updateVariant(variant, updateUrl);
    };
    options.forEach((option) => option.addEventListener('change', () => {
      const selection = selectedOptions();
      updateVariant(data.variants.find((variant) => variant.options.every((value, index) => value === selection[index])));
    }));
    select.addEventListener('change', () => updateVariant(data.variants.find((variant) => String(variant.id) === select.value)));
    if (hasBundles) {
      section.dataset.bundles = 'true';
      section.querySelector('[data-single-options]')?.setAttribute('hidden', '');
      quantity.closest('.product-quantity').hidden = true;
      section.querySelectorAll('[data-bundle-choice]').forEach((radio) => radio.addEventListener('change', () => chooseTier(Number(radio.value))));
      section.querySelectorAll('[data-bundle-option]').forEach((input) => input.addEventListener('change', () => {
        const values = Array.from(activeCard().querySelectorAll('[data-bundle-option]')).map((option) => option.value);
        values.forEach((value, index) => { rememberedSizes[index] = value; });
        updateVariant(selectedTier.variants.find((variant) => variant.options.every((value, index) => value === values[index])));
      }));
      const url = new URL(window.location.href);
      const directTier = tiers.find((tier) => new URL(tier.url, window.location.origin).pathname === url.pathname);
      const count = Number(url.searchParams.get('bundle')) || (directTier?.count > 1 ? directTier.count : 2);
      const variantId = url.searchParams.get('bundle_variant') || (directTier?.count > 1 ? url.searchParams.get('variant') : count === 1 ? initialVariant?.id : null);
      chooseTier(count, variantId, false);
    }
    section.querySelectorAll('[data-quantity-direction]').forEach((button) => button.addEventListener('click', () => {
      const minimum = Number(quantity.min) || 1;
      const step = Number(quantity.step) || 1;
      let value = Math.max(minimum, (Number(quantity.value) || minimum) + Number(button.dataset.quantityDirection) * step);
      if (quantity.max) value = Math.min(Number(quantity.max), value);
      quantity.value = value;
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
    }));
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (busy || !selectedVariant?.available || !form.reportValidity()) return;
      busy = true;
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      submitText.textContent = data.strings.adding;
      error.hidden = true;
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || data.strings.error);
        window.location.assign(section.dataset.cartUrl || `${root}cart`);
      } catch (failure) {
        error.textContent = failure instanceof Error && failure.message !== 'Failed to fetch' ? failure.message : data.strings.error;
        error.hidden = false;
        busy = false;
        submit.disabled = !selectedVariant?.available;
        submit.removeAttribute('aria-busy');
        submitText.textContent = selectedVariant?.available ? data.strings.add : data.strings.soldOut;
      }
    });
    if (!hasBundles) updateVariant(selectedVariant, false);
    const sticky = section.querySelector('[data-product-sticky]');
    sticky?.querySelector('a')?.addEventListener('click', event => {
      event.preventDefault();
      const target = section.querySelector('.product-info');
      window.scrollTo({ top: window.scrollY + target.getBoundingClientRect().top - 24, behavior: 'instant' });
    });
    if (sticky && 'IntersectionObserver' in window) {
      const updateSticky = () => { sticky.hidden = form.getBoundingClientRect().bottom >= 0; };
      const observer = new IntersectionObserver(updateSticky, { threshold: 0 });
      let frame;
      const onScroll = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(updateSticky); };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      updateSticky();
      observer.observe(form);
      document.addEventListener('shopify:section:unload', (event) => { if (event.target.contains(section)) observer.disconnect(); }, { once: true });
    }
  };
  const initializeAll = (root = document) => root.querySelectorAll('[data-product-section]').forEach(initialize);
  initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
